import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Money,
  TableScroll,
} from '@mlm/ui';
import { downloadRevenueCsv, getRevenue } from '../lib/api';
import { MoneyStat, Sparkline, Stat } from '../components/stats';

const RANGES = [
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '365d', label: 'Last 12 months', days: 365 },
] as const;

type RangeKey = (typeof RANGES)[number]['key'] | 'custom';

function presetWindow(key: RangeKey): { from: string; to: string } {
  const preset = RANGES.find((r) => r.key === key);
  const days = preset?.days ?? 30;
  const to = new Date();
  const from = new Date(to.getTime() - days * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

/** A yyyy-mm-dd input, read as a LOCAL day and widened to cover the whole `to` day. */
function dayBounds(fromDay: string, toDay: string): { from: string; to: string } | null {
  const from = new Date(`${fromDay}T00:00:00`);
  const to = new Date(`${toDay}T23:59:59.999`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) return null;
  return { from: from.toISOString(), to: to.toISOString() };
}

function todayInput(offsetDays = 0): string {
  const d = new Date(Date.now() - offsetDays * 86_400_000);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

/**
 * Creator revenue (LMS Phase 7).
 *
 * TWO sources on one page, labelled apart on purpose:
 *  - "Earned" is the WALLET ledger. It is the truth about what was credited.
 *  - "By course" is derived from orders, because the ledger holds one row per
 *    ORDER and cannot attribute money to a course.
 * They can disagree when a refund lands in a different window than its sale —
 * so the table is never summed to replace the headline figure.
 *
 * `gross` and `fee` appear only when the tenant shows the platform fee to this
 * creator. They travel together: publishing gross beside net reveals the fee by
 * subtraction, so hiding one alone would hide nothing.
 */
export function RevenuePage() {
  const [range, setRange] = useState<RangeKey>('30d');
  // The custom window is only APPLIED on the button, so typing a half-finished
  // date never fires a query (and never rewrites the chart under the cursor).
  const [customDraft, setCustomDraft] = useState({ from: todayInput(30), to: todayInput(0) });
  const [customApplied, setCustomApplied] = useState<{ from: string; to: string } | null>(null);
  const customInvalid = dayBounds(customDraft.from, customDraft.to) === null;

  const params = useMemo(
    () => (range === 'custom' && customApplied ? customApplied : presetWindow(range)),
    [range, customApplied],
  );
  const q = useQuery({
    queryKey: ['creator-revenue', params.from, params.to],
    queryFn: () => getRevenue(params),
  });
  const data = q.data;

  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">Revenue</h1>
        <Button variant="outline" size="sm" onClick={() => void downloadRevenueCsv(params)}>
          Export CSV
        </Button>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setRange(r.key)}
              className={[
                'rounded-full border px-3 py-1 text-sm',
                range === r.key ? 'bg-accent font-medium' : 'hover:bg-accent',
              ].join(' ')}
            >
              {r.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              setRange('custom');
              // First switch applies the draft straight away, so the page never
              // sits on "custom" showing the last preset's numbers.
              if (!customApplied) setCustomApplied(dayBounds(customDraft.from, customDraft.to));
            }}
            className={[
              'rounded-full border px-3 py-1 text-sm',
              range === 'custom' ? 'bg-accent font-medium' : 'hover:bg-accent',
            ].join(' ')}
          >
            Custom range
          </button>
        </div>
        {range === 'custom' && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="revenue-from">
                From
              </label>
              <Input
                id="revenue-from"
                type="date"
                className="w-40"
                value={customDraft.from}
                max={customDraft.to}
                onChange={(e) => setCustomDraft((d) => ({ ...d, from: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground" htmlFor="revenue-to">
                To
              </label>
              <Input
                id="revenue-to"
                type="date"
                className="w-40"
                value={customDraft.to}
                min={customDraft.from}
                max={todayInput(0)}
                onChange={(e) => setCustomDraft((d) => ({ ...d, to: e.target.value }))}
              />
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={customInvalid}
              onClick={() => setCustomApplied(dayBounds(customDraft.from, customDraft.to))}
            >
              Apply
            </Button>
            {customInvalid && (
              <p className="text-xs text-destructive">
                Pick a start date on or before the end date.
              </p>
            )}
          </div>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : q.isError ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            Could not load your revenue.{' '}
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => q.refetch()}
            >
              Try again
            </button>
          </CardContent>
        </Card>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MoneyStat label="Earned (wallet)" amount={data.totals.net} hint="Credited to you" />
            {data.showFee && data.totals.gross !== undefined && (
              <MoneyStat label="Gross sales" amount={data.totals.gross} hint="After coupons" />
            )}
            {data.showFee && data.totals.fee !== undefined && (
              <MoneyStat label="Platform fee" amount={data.totals.fee} />
            )}
            <Stat label="Sales" value={String(data.totals.orders)} />
            <Stat
              label="Refunds"
              value={String(data.totals.refunds)}
              hint={
                Number(data.totals.refundedNet) > 0
                  ? `−${Number(data.totals.refundedNet).toFixed(2)} reversed`
                  : undefined
              }
            />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Earned per day</CardTitle>
            </CardHeader>
            <CardContent>
              <Sparkline
                points={data.series.map((p) => ({
                  date: p.date,
                  value: Number(p.net),
                  label: `${p.date}: ${Number(p.net).toFixed(2)} earned`,
                }))}
                emptyText="No sales in this period."
                ariaLabel="Earned per day"
              />
              <p className="mt-2 text-xs text-muted-foreground">
                Dated in UTC, from your wallet ledger.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>By course</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {data.courses.length === 0 ? (
                <p className="p-6 text-center text-muted-foreground">No sales in this period.</p>
              ) : (
                <TableScroll>
                  <table className="w-full text-sm">
                    <thead className="border-b text-left text-muted-foreground">
                      <tr>
                        <th className="p-3">Course</th>
                        <th className="p-3 text-right">Units</th>
                        <th className="p-3 text-right">Coupons</th>
                        {data.showFee && <th className="p-3 text-right">Gross</th>}
                        {data.showFee && <th className="p-3 text-right">Fee</th>}
                        <th className="p-3 text-right">Net</th>
                        <th className="p-3 text-right">Refunds</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.courses.map((c) => (
                        <tr key={c.courseId} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="p-3">{c.title}</td>
                          <td className="p-3 text-right tabular-nums">{c.units}</td>
                          <td className="p-3 text-right">
                            <Money amount={c.coupon} direction="neutral" />
                          </td>
                          {data.showFee && (
                            <td className="p-3 text-right">
                              <Money amount={c.gross ?? '0'} direction="neutral" />
                            </td>
                          )}
                          {data.showFee && (
                            <td className="p-3 text-right">
                              <Money amount={c.fee ?? '0'} direction="neutral" />
                            </td>
                          )}
                          <td className="p-3 text-right">
                            <Money amount={c.net} direction="neutral" />
                          </td>
                          <td className="p-3 text-right tabular-nums">{c.refunds}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              )}
            </CardContent>
          </Card>
          <p className="text-xs text-muted-foreground">
            &ldquo;Earned&rdquo; comes from your wallet ledger; &ldquo;By course&rdquo; is derived
            from orders. A refund recorded in a different period than its sale can make the two
            differ.
          </p>
        </>
      )}
    </div>
  );
}
